import dayjs, { Dayjs } from 'dayjs';
import { VisitaEstado } from '../../consts/visitasStatus';

export const TIPO_VISITA_VEROSIMILITUD_ID = 457;
export const TIPO_VISITA_SEGUIMIENTO_VEROSIMILITUD_ID = 473;
const ID_TIPO_RESPUESTA_INCUMPLE = 2;

// Estados en los que un Seguimiento Verosimilitud bloquea el alta de otro en el mismo
// establecimiento (SMP-351). A diferencia del chequeo general, RECHAZADO y REALIZADA
// (En Revisión) también cuentan como activos.
const ESTADOS_SEGUIMIENTO_ACTIVOS: VisitaEstado[] = [
    VisitaEstado.SIN_ASIGNAR,
    VisitaEstado.RECHAZADO,
    VisitaEstado.PENDIENTE,
    VisitaEstado.PROGRAMADA,
    VisitaEstado.EN_PROCESO,
    VisitaEstado.REALIZADA
];

const tieneActividad = (visita: any, idTipoVisita: number) =>
    (visita?.actividades || []).some((actividad: any) => actividad?.tipoVisita?.id === idTipoVisita);

export const tieneSeguimientoVerosimilitudActivo = (visitas: any[]): boolean =>
    visitas.some(
        (visita) =>
            tieneActividad(visita, TIPO_VISITA_SEGUIMIENTO_VEROSIMILITUD_ID) &&
            ESTADOS_SEGUIMIENTO_ACTIVOS.includes(visita?.estado)
    );

// "Última verosimilitud" del establecimiento: la aprobada por BO (PRESENTADO) con fecha de
// visita más reciente. Mismo criterio que getIdUltimaVerosimilitudAprobada en el backend.
const getUltimaVerosimilitudAprobada = (visitas: any[]) =>
    visitas
        .filter(
            (visita) =>
                visita?.estado === VisitaEstado.PRESENTADO &&
                tieneActividad(visita, TIPO_VISITA_VEROSIMILITUD_ID)
        )
        .sort((a, b) => {
            const porFecha =
                (b?.fechaVisita ? dayjs(b.fechaVisita).valueOf() : 0) -
                (a?.fechaVisita ? dayjs(a.fechaVisita).valueOf() : 0);
            return porFecha !== 0 ? porFecha : (b?.id ?? 0) - (a?.id ?? 0);
        })[0];

export type PrecargaSeguimiento =
    | { resultado: 'SIN_VEROSIMILITUD' }
    | { resultado: 'SIN_INCUMPLIMIENTOS' }
    | {
          resultado: 'OK';
          idProveedor: number | null;
          preventor: { id: number; nombre: string; value: number } | null;
          fechaSeguimiento: Dayjs | null;
      };

/**
 * Datos con los que se precarga el alta de un Seguimiento Verosimilitud, a partir de las
 * visitas ya cargadas del establecimiento. La fecha de seguimiento es única por formulario
 * desde HU-1.0.4; en registros previos con una fecha por ítem se toma la más tardía.
 */
export const resolverPrecargaSeguimiento = (visitas: any[]): PrecargaSeguimiento => {
    const verosimilitud = getUltimaVerosimilitudAprobada(visitas);
    if (!verosimilitud) return { resultado: 'SIN_VEROSIMILITUD' };

    const source = verosimilitud.raw || verosimilitud;
    const incumplidas = (source?.verosimilitud?.respuestas || []).filter(
        (respuesta: any) => respuesta?.idTipoRespuesta === ID_TIPO_RESPUESTA_INCUMPLE
    );
    if (incumplidas.length === 0) return { resultado: 'SIN_INCUMPLIMIENTOS' };

    const fechasSeguimiento: Dayjs[] = incumplidas
        .map((respuesta: any) => respuesta?.fechaSeguimiento)
        .filter(Boolean)
        .map((fecha: string) => dayjs(String(fecha).split('T')[0]));

    const fechaSeguimiento = fechasSeguimiento.reduce<Dayjs | null>(
        (maxima, fecha) => (!maxima || fecha.isAfter(maxima) ? fecha : maxima),
        null
    );

    const preventor = source?.preventor;

    return {
        resultado: 'OK',
        idProveedor: source?.proveedorId ?? preventor?.proveedor?.id ?? null,
        preventor: preventor?.idUsuario
            ? {
                  id: preventor.idUsuario,
                  nombre: `${preventor.nombre || ''} ${preventor.apellido || ''}`.trim(),
                  value: preventor.idUsuario
              }
            : null,
        fechaSeguimiento
    };
};
